import React from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';

const ProductsSection = ({
  brazos,
  filteredBrazos,
  categories,
  selectedCategory,
  setSelectedCategory,
  brazosSearchTerm,
  setBrazosSearchTerm,
  showAllBrazos,
  setShowAllBrazos,
  loadingProducts,
  openLightbox,
  openProductDetail,
  getImageUrl,
  BRAZOS_INITIAL_COUNT
}) => {
  return (
    <section id="productos" className="py-20 px-6 bg-cream">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-chocolate mb-4">Nuestros Brazos Gitanos</h2>
          <p className="text-xl text-chocolate-light max-w-2xl mx-auto">
            Más de 40 sabores únicos, elaborados artesanalmente cada día
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-chocolate-light w-5 h-5" />
            <Input
              type="text"
              placeholder="Buscar brazos por nombre..."
              value={brazosSearchTerm}
              onChange={(e) => setBrazosSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 w-full border-2 border-caramel focus:border-chocolate rounded-lg text-chocolate placeholder:text-chocolate-light"
            />
            {brazosSearchTerm && (
              <button
                onClick={() => setBrazosSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-chocolate-light hover:text-chocolate"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center mb-12 px-2 overflow-x-auto">
          <div className="flex gap-1 md:gap-2 bg-white rounded-full p-1 md:p-2 shadow-lg">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                  setShowAllBrazos(false);
                }}
                className={`px-3 py-1.5 md:px-6 md:py-3 rounded-full font-medium transition-all text-xs md:text-base whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-[#d4a574] text-white shadow-md'
                    : 'text-chocolate hover:bg-cream'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loadingProducts ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-caramel" />
            <span className="ml-4 text-chocolate text-lg">Cargando productos...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {(showAllBrazos ? filteredBrazos : filteredBrazos.slice(0, BRAZOS_INITIAL_COUNT)).map((brazo) => (
              <Card key={brazo.id} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group flex flex-col h-full">
                <div 
                  className="aspect-square overflow-hidden cursor-pointer relative"
                  onClick={() => openLightbox(getImageUrl(brazo.image), brazo.name)}
                >
                  <img 
                    src={getImageUrl(brazo.image)} 
                    alt={brazo.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  
                  {/* Badge "Bajo Pedido" */}
                  {brazo.bajoPedido && (
                    <div className="absolute top-3 right-3 z-10">
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1.5 rounded-lg shadow-lg border-2 border-white">
                        <span className="text-xs font-bold">🏷️ BAJO PEDIDO</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="bg-white/90 text-chocolate px-3 py-1 rounded-full text-sm font-medium">
                      🔍 Ver imagen
                    </span>
                  </div>
                </div>
                <CardContent className="p-4 md:p-6 flex flex-col flex-grow">
                  <div className="flex-grow space-y-2 md:space-y-3">
                    <h3 className="text-base md:text-xl font-bold text-chocolate line-clamp-1">{brazo.name}</h3>
                    <p className="text-xs md:text-sm text-chocolate-light line-clamp-2">{brazo.description}</p>
                    {brazo.category === 'Menú de la Reina' && (
                      <div className="bg-red-50 border border-red-300 rounded-lg p-2 mt-2">
                        <p className="text-xs font-semibold text-red-900">⏰ Aviso:</p>
                        <p className="text-xs text-red-800">
                          Este pedido requiere al menos 24 horas para su preparación y debe recibir confirmación de inicio de preparación
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    onClick={() => openProductDetail(brazo, 'brazo')}
                    className="w-full bg-[#d4a574] hover:bg-[#c49564] text-white mt-4 text-sm md:text-base min-h-[44px] transition-colors duration-200"
                  >
                    Ver Detalles
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Show More / Show Less Button */}
        {filteredBrazos.length > BRAZOS_INITIAL_COUNT && (
          <div className="text-center mt-8">
            <Button
              onClick={() => setShowAllBrazos(!showAllBrazos)}
              variant="outline"
              className="px-8 py-3 border-2 border-[#d4a574] text-[#d4a574] hover:bg-[#d4a574] hover:text-white transition-all duration-200"
            >
              {showAllBrazos ? (
                <>
                  Ver Menos
                  <svg className="w-5 h-5 ml-2 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              ) : (
                <>
                  Ver Todos ({filteredBrazos.length} sabores)
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;
